import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { waldflaechenAPI, constantsAPI } from '@/lib/api';
import api from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  TreePine,
  Plus,
  MapPin,
  Ruler,
  Trash2,
  ExternalLink,
  Calculator,
  Leaf,
  Award,
  TrendingUp,
  Info
} from 'lucide-react';

const BUNDESLAENDER = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen",
  "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen",
  "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen",
  "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const BAUMARTEN = [
  "Fichte", "Kiefer", "Buche", "Eiche", "Tanne", "Lärche", "Douglasie",
  "Birke", "Ahorn", "Esche", "Erle", "Pappel", "Mischbestand"
];

const MeinWaldPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [waldflaechen, setWaldflaechen] = useState([]);
  const [co2Data, setCo2Data] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('flaechen');
  const [formData, setFormData] = useState({
    name: '',
    bundesland: '',
    ort: '',
    flaeche_ha: '',
    baumarten: [],
    beschreibung: ''
  });

  useEffect(() => {
    fetchWaldflaechen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWaldflaechen = async () => {
    try {
      const response = await waldflaechenAPI.list();
      setWaldflaechen(response.data);
      
      // Fetch CO2 data for each forest
      const co2Results = {};
      for (const wald of response.data) {
        try {
          const co2Response = await api.post('/api/tools/co2-calculation', {
            flaeche_ha: wald.flaeche_ha,
            baumart: wald.baumarten?.[0] || null,
            years: 10
          });
          co2Results[wald.id] = co2Response.data;
        } catch (err) {
          console.error('CO2 calc error for', wald.id, err);
        }
      }
      setCo2Data(co2Results);
    } catch (error) {
      console.error('Error fetching forests:', error);
      toast.error(t('toasts.walddatenLadeFehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleBaumart = (baumart) => {
    setFormData(prev => ({
      ...prev,
      baumarten: prev.baumarten.includes(baumart)
        ? prev.baumarten.filter(b => b !== baumart)
        : [...prev.baumarten, baumart]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.bundesland || !formData.flaeche_ha) {
      toast.error(t('toasts.pflichtfelderAusfuellen'));
      return;
    }

    setSubmitting(true);
    try {
      await waldflaechenAPI.create({
        name: formData.name,
        bundesland: formData.bundesland,
        ort: formData.ort || null,
        flaeche_ha: parseFloat(formData.flaeche_ha),
        baumarten: formData.baumarten,
        beschreibung: formData.beschreibung || null
      });
      
      toast.success(t('toasts.waldHinzugefuegt'));
      setShowDialog(false);
      setFormData({
        name: '',
        bundesland: '',
        ort: '',
        flaeche_ha: '',
        baumarten: [],
        beschreibung: ''
      });
      fetchWaldflaechen();
    } catch (error) {
      console.error('Error creating forest:', error);
      toast.error(t('toasts.walddatenSpeichernFehler'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Waldfläche wirklich löschen?')) return;
    
    try {
      await waldflaechenAPI.delete(id);
      toast.success(t('toasts.waldGeloescht'));
      fetchWaldflaechen();
    } catch (error) {
      console.error('Error deleting forest:', error);
      toast.error(t('toasts.waldLoeschFehler'));
    }
  };

  const totalArea = waldflaechen.reduce((sum, w) => sum + w.flaeche_ha, 0);
  
  // Calculate total CO2 values
  const totalCO2 = Object.values(co2Data).reduce((sum, data) => sum + (data?.total_storage_tons || 0), 0);
  const totalCertificateValue = Object.values(co2Data).reduce((sum, data) => sum + (data?.estimated_certificate_value || 0), 0);
  const totalYearlyCO2 = Object.values(co2Data).reduce((sum, data) => sum + (data?.yearly_storage_tons || 0), 0);

  const formatNumber = (num) => new Intl.NumberFormat('de-DE').format(num);
  const formatCurrency = (price) => new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="mein-wald-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-gray-900">
              Mein Wald
            </h1>
            <p className="text-gray-500 mt-1">
              Verwalten Sie Ihre Waldflächen und CO₂-Potenzial
            </p>
          </div>
          
          <Button
            onClick={() => setShowDialog(true)}
            className="bg-primary hover:bg-primary-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Waldfläche hinzufügen
          </Button>
        </div>

        {/* Tabs */}
        {waldflaechen.length > 0 && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="flaechen" className="flex items-center gap-2">
                <TreePine className="h-4 w-4" />
                Waldflächen
              </TabsTrigger>
              <TabsTrigger value="co2" className="flex items-center gap-2" data-testid="co2-dashboard-tab">
                <Leaf className="h-4 w-4" />
                CO₂-Dashboard
              </TabsTrigger>
            </TabsList>

            {/* Waldflächen Tab */}
            <TabsContent value="flaechen" className="space-y-6 mt-6">
              {/* Stats */}
              <div className="grid sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Waldflächen</p>
                        <p className="text-2xl font-bold text-gray-900">{waldflaechen.length}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-primary/10">
                        <TreePine className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Gesamtfläche</p>
                        <p className="text-2xl font-bold text-gray-900">{totalArea.toFixed(1)} ha</p>
                      </div>
                      <div className="p-3 rounded-xl bg-secondary/10">
                        <Ruler className="h-6 w-6 text-secondary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <a 
                      href="https://waldrechner.eu"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between group"
                    >
                      <div>
                        <p className="text-sm text-gray-500">Waldwert berechnen</p>
                        <p className="text-lg font-semibold text-primary group-hover:underline">
                          waldrechner.eu
                        </p>
                      </div>
                      <div className="p-3 rounded-xl bg-accent/10">
                        <Calculator className="h-6 w-6 text-accent" />
                      </div>
                    </a>
                  </CardContent>
                </Card>
              </div>

              {/* Forest List */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {waldflaechen.map((wald) => (
                  <Card key={wald.id} className="group hover:shadow-lift transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-2 rounded-lg bg-secondary/10">
                          <TreePine className="h-5 w-5 text-secondary" />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(wald.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <h3 className="font-semibold text-lg text-gray-900 mb-2">
                        {wald.name}
                      </h3>
                      
                      <div className="space-y-2 text-sm text-gray-500 mb-4">
                        <p className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {wald.ort ? `${wald.ort}, ` : ''}{wald.bundesland}
                        </p>
                        <p className="flex items-center gap-2">
                          <Ruler className="h-4 w-4" />
                          {wald.flaeche_ha} Hektar
                        </p>
                      </div>
                      
                      {wald.baumarten?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {wald.baumarten.slice(0, 3).map((baumart) => (
                            <Badge key={baumart} variant="secondary" className="bg-secondary/10 text-secondary text-xs">
                              {baumart}
                            </Badge>
                          ))}
                          {wald.baumarten.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{wald.baumarten.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* CO2 Mini Display */}
                      {co2Data[wald.id] && (
                        <div className="p-3 bg-green-50 rounded-lg mb-4">
                          <p className="text-xs text-green-600 mb-1">CO₂-Potenzial (10 Jahre)</p>
                          <p className="font-semibold text-green-800">
                            {formatNumber(co2Data[wald.id].total_storage_tons)} t CO₂
                          </p>
                        </div>
                      )}

                      <div className="pt-4 border-t border-warm-200">
                        <Link to={`/projekte/neu?wald=${wald.id}`}>
                          <Button variant="outline" size="sm" className="w-full">
                            Projekt erstellen
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* CO2 Dashboard Tab */}
            <TabsContent value="co2" className="space-y-6 mt-6" data-testid="co2-dashboard">
              {/* CO2 Overview Stats */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600">Jährliche CO₂-Speicherung</p>
                        <p className="text-2xl font-bold text-green-800">{formatNumber(totalYearlyCO2)} t</p>
                        <p className="text-xs text-green-600 mt-1">pro Jahr</p>
                      </div>
                      <div className="p-3 rounded-xl bg-green-100">
                        <Leaf className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600">Gesamt (10 Jahre)</p>
                        <p className="text-2xl font-bold text-green-800">{formatNumber(totalCO2)} t</p>
                        <p className="text-xs text-green-600 mt-1">CO₂-Speicherung</p>
                      </div>
                      <div className="p-3 rounded-xl bg-green-100">
                        <TrendingUp className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-600">Zertifikatswert</p>
                        <p className="text-2xl font-bold text-amber-800">{formatCurrency(totalCertificateValue)}</p>
                        <p className="text-xs text-amber-600 mt-1">geschätzt (10 Jahre)</p>
                      </div>
                      <div className="p-3 rounded-xl bg-amber-100">
                        <Award className="h-6 w-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">CO₂-Preis</p>
                        <p className="text-2xl font-bold text-gray-900">70€</p>
                        <p className="text-xs text-gray-500 mt-1">pro Tonne (EU-ETS)</p>
                      </div>
                      <div className="p-3 rounded-xl bg-gray-100">
                        <Calculator className="h-6 w-6 text-gray-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* CO2 Info Banner */}
              <div className="p-4 bg-green-100/50 border border-green-200 rounded-xl flex items-start gap-3">
                <Info className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800 mb-1">Was bedeutet das?</p>
                  <p className="text-sm text-green-700">
                    Ihre Waldflächen speichern jährlich ca. <strong>{formatNumber(totalYearlyCO2)} Tonnen CO₂</strong>. 
                    Das entspricht dem CO₂-Ausstoß von etwa <strong>{Math.round(totalYearlyCO2 / 11)} durchschnittlichen Pkw-Fahrern</strong> pro Jahr 
                    (bei ca. 11t CO₂/Auto/Jahr). Der geschätzte Zertifikatswert basiert auf dem aktuellen EU-ETS Preis.
                  </p>
                </div>
              </div>

              {/* CO2 per Forest Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">CO₂-Potenzial nach Waldfläche</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {waldflaechen.map((wald) => {
                      const data = co2Data[wald.id];
                      if (!data) return null;
                      
                      const percentage = totalCO2 > 0 ? (data.total_storage_tons / totalCO2) * 100 : 0;
                      
                      return (
                        <div key={wald.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <TreePine className="h-4 w-4 text-secondary" />
                              <span className="font-medium">{wald.name}</span>
                              <span className="text-sm text-gray-500">({wald.flaeche_ha} ha)</span>
                            </div>
                            <div className="text-right">
                              <span className="font-semibold text-green-700">{formatNumber(data.total_storage_tons)} t</span>
                              <span className="text-sm text-gray-500 ml-2">({formatCurrency(data.estimated_certificate_value)})</span>
                            </div>
                          </div>
                          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>Baumart: {data.baumart}</span>
                            <span>{data.storage_rate_per_ha_year} t CO₂/ha/Jahr</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Call to Action */}
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900 mb-1">
                        Interesse an CO₂-Zertifikaten?
                      </h3>
                      <p className="text-gray-600">
                        Erstellen Sie ein CO₂-Klimaschutzprojekt und finden Sie Partner für die Zertifizierung.
                      </p>
                    </div>
                    <Link to="/projekte/neu">
                      <Button className="bg-primary hover:bg-primary-600 whitespace-nowrap">
                        <Leaf className="mr-2 h-4 w-4" />
                        CO₂-Projekt starten
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Empty state when no forests */}
        {!loading && waldflaechen.length === 0 && (
          <Card className="text-center py-16">
            <CardContent>
              <TreePine className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-xl text-gray-900 mb-2">
                Keine Waldflächen gespeichert
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Speichern Sie Ihre Waldflächen hier, um schneller Projekte zu erstellen
                und Ihr CO₂-Potenzial zu berechnen.
              </p>
              <Button
                onClick={() => setShowDialog(true)}
                className="bg-primary hover:bg-primary-600"
              >
                <Plus className="mr-2 h-4 w-4" />
                Erste Waldfläche hinzufügen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-primary">Laden...</div>
          </div>
        )}
      </div>

      {/* Add Forest Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Waldfläche hinzufügen</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="z.B. Nordwald oder Parzelle 12"
                data-testid="forest-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bundesland *</Label>
                <Select
                  value={formData.bundesland}
                  onValueChange={(v) => handleChange('bundesland', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('placeholders.auswaehlen')} />
                  </SelectTrigger>
                  <SelectContent>
                    {BUNDESLAENDER.map((land) => (
                      <SelectItem key={land} value={land}>{land}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ort</Label>
                <Input
                  value={formData.ort}
                  onChange={(e) => handleChange('ort', e.target.value)}
                  placeholder="z.B. Garmisch"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Fläche in Hektar *</Label>
              <Input
                type="number"
                step="0.1"
                value={formData.flaeche_ha}
                onChange={(e) => handleChange('flaeche_ha', e.target.value)}
                placeholder="z.B. 12.5"
              />
            </div>

            <div className="space-y-2">
              <Label>Baumarten</Label>
              <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                {BAUMARTEN.map((baumart) => (
                  <div
                    key={baumart}
                    onClick={() => toggleBaumart(baumart)}
                    className={`flex items-center gap-1 p-2 rounded border cursor-pointer transition-colors text-xs ${
                      formData.baumarten.includes(baumart)
                        ? 'bg-secondary/10 border-secondary'
                        : 'border-warm-200 hover:border-secondary/50'
                    }`}
                  >
                    <Checkbox checked={formData.baumarten.includes(baumart)} />
                    {baumart}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea
                value={formData.beschreibung}
                onChange={(e) => handleChange('beschreibung', e.target.value)}
                placeholder={t('placeholders.optionaleNotizen')}
                rows={2}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-primary hover:bg-primary-600"
            >
              {submitting ? 'Wird gespeichert...' : 'Speichern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default MeinWaldPage;
